import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Container,
  Divider,
  Chip,
  Menu,
  MenuItem,
  Paper,
  Grid,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Stack,
} from '@mui/material';

import { Link as RouterLink, useLocation } from 'react-router-dom';

import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import BuildIcon from '@mui/icons-material/Build';
import CallIcon from '@mui/icons-material/Call';
import HomeIcon from '@mui/icons-material/Home';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined';
import RealEstateAgent from '@mui/icons-material/RealEstateAgent';
import ManageAccountsOutlined from '@mui/icons-material/ManageAccountsOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo.png`;

export default function Header() {
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Desktop dropdown anchors
  const [servicesAnchor, setServicesAnchor] = useState(null);
  const [companyAnchor, setCompanyAnchor] = useState(null);

  // Mobile collapses
  const [openServicesMobile, setOpenServicesMobile] = useState(true);
  const [openCompanyMobile, setOpenCompanyMobile] = useState(false);

  const toggleMobileMenu = () => setIsMobileMenuOpen((p) => !p);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  // NAV STRUCTURE (kept to your existing routes)
  const services = [
    { label: 'Buyers', href: '/buyers', icon: <PeopleAltOutlined fontSize="small" /> },
    { label: 'Sellers', href: '/sellers', icon: <ManageAccountsOutlined fontSize="small" /> },
    { label: 'Properties', href: '/properties', icon: <ManageAccountsOutlined fontSize="small" /> },
    { label: 'Landlords', href: '/landlords', icon: <RealEstateAgent fontSize="small" /> },
    { label: 'Tenants', href: '/tenants', icon: <HomeIcon fontSize="small" /> },
  ];

  const company = [
    { label: 'About', href: '/about', icon: <InfoOutlined fontSize="small" /> },
    { label: 'Report a Repair', href: '/repairs', icon: <BuildIcon fontSize="small" /> },
    { label: 'Contact', href: '/contact', icon: <CallIcon fontSize="small" /> },
  ];

  return (
    <>
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        {/* Top info strip */}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 0.5,
          }}
        >
          <Container maxWidth="lg">
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Registered office: 142 Victoria Road, Middlesbrough, TS1 3HU · Est. 2019 · SIC 68320
              </Typography>
              <Chip
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,.15)', color: 'primary.contrastText' }}
                label="4.74 / 5 — 1,571 Google Reviews"
              />
            </Stack>
          </Container>
        </Box>

        <AppBar
          position="static"
          color="transparent"
          elevation={scrolled ? 6 : 0}
          sx={{
            backdropFilter: scrolled ? 'saturate(1.2) blur(8px)' : 'none',
            backgroundColor: scrolled ? 'rgba(255,255,255,0.8)' : 'transparent',
            transition: 'all .25s ease',
            borderBottom: scrolled ? '1px solid rgba(0,0,0,0.06)' : 'transparent',
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ minHeight: 72 }}>
            {/* Logo / Brand */}
            <Box
              component={RouterLink}
              to="/"
              aria-label="MRM Lettings home"
              sx={{
                flexGrow: { xs: 1, md: 0 },
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'text.primary',
                mr: 2,
              }}
            >
              <Box
                component="img"
                src={LOGO_SRC}
                alt="MRM Lettings logo"
                sx={{
                  height: 44,
                  width: 'auto',
                }}
              />
            </Box>

            {/* Desktop NAV */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1,
                ml: 2,
              }}
            >
              <Button
                component={RouterLink}
                to="/"
                color={isActive('/') ? 'primary' : 'inherit'}
                sx={{
                  fontWeight: 600,
                  ...(isActive('/') && { bgcolor: 'action.hover' }),
                }}
                startIcon={<HomeIcon />}
              >
                Home
              </Button>

              {/* Services dropdown */}
              <Button
                onClick={(e) => setServicesAnchor(e.currentTarget)}
                endIcon={<ExpandMoreIcon />}
                sx={{ fontWeight: 600, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                aria-controls={Boolean(servicesAnchor) ? 'services-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(servicesAnchor) ? 'true' : undefined}
              >
                Services
              </Button>
              <Menu
                id="services-menu"
                anchorEl={servicesAnchor}
                open={Boolean(servicesAnchor)}
                onClose={() => setServicesAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{ sx: { p: 2, borderRadius: 2, minWidth: 420 } }}
              >
                <Paper elevation={0} sx={{ p: 1 }}>
                  <Grid container spacing={0.5}>
                    {services.map((s) => (
                      <Grid item xs={12} sm={6} key={s.href}>
                        <MenuItem
                          component={RouterLink}
                          to={s.href}
                          onClick={() => setServicesAnchor(null)}
                          sx={{
                            py: 1.25,
                            borderRadius: 1,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Box sx={{ mr: 1.25 }}>{s.icon}</Box>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {s.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Guidance and support for {s.label.toLowerCase()}.
                            </Typography>
                          </Box>
                        </MenuItem>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Menu>

              {/* Company dropdown */}
              <Button
                onClick={(e) => setCompanyAnchor(e.currentTarget)}
                endIcon={<ExpandMoreIcon />}
                sx={{ fontWeight: 600, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                aria-controls={Boolean(companyAnchor) ? 'company-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(companyAnchor) ? 'true' : undefined}
              >
                Company
              </Button>
              <Menu
                id="company-menu"
                anchorEl={companyAnchor}
                open={Boolean(companyAnchor)}
                onClose={() => setCompanyAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{ sx: { p: 1, borderRadius: 2, minWidth: 260 } }}
              >
                {company.map((c) => (
                  <MenuItem
                    key={c.href}
                    component={RouterLink}
                    to={c.href}
                    onClick={() => setCompanyAnchor(null)}
                    sx={{ py: 1.1, borderRadius: 1 }}
                  >
                    <Box sx={{ mr: 1.25 }}>{c.icon}</Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {c.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>

              <Button
                component={RouterLink}
                to="/payments"
                startIcon={<AccountBalanceIcon />}
                color={isActive('/payments') ? 'primary' : 'inherit'}
                sx={{
                  fontWeight: 600,
                  ...(isActive('/payments') && { bgcolor: 'action.hover' }),
                }}
              >
                Payments
              </Button>
            </Box>

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop Actions */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Chip
                size="small"
                variant="outlined"
                label="4.74 / 5 — 1,571 reviews"
                sx={{ borderColor: 'divider' }}
              />
              <Button
                component={RouterLink}
                to="/repairs"
                startIcon={<BuildIcon />}
                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                Report a Repair
              </Button>
              <Button
                component={RouterLink}
                to="/sellers/valuation"
                variant="contained"
                color="primary"
                sx={{ fontWeight: 800 }}
              >
                Request Valuation
              </Button>
            </Stack>

            {/* Mobile Toggle */}
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              sx={{ display: { md: 'none' } }}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
            </Toolbar>
          </Container>

          {/* Subtle divider when scrolled */}
          {scrolled && <Divider />}
        </AppBar>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={isMobileMenuOpen}
        onClose={toggleMobileMenu}
        PaperProps={{ sx: { width: '85%', maxWidth: 360 } }}
      >
        <Box role="presentation" sx={{ p: 2 }}>
          {/* Brand mini */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <Box
              component="img"
              src={LOGO_SRC}
              alt="MRM Lettings logo"
              sx={{ height: 36, width: 'auto' }}
            />
            <Typography variant="subtitle1" fontWeight={800}>
              MRM Lettings
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Local team, single-office service, deep area knowledge
          </Typography>
          <Chip size="small" label="4.74 / 5 — 1,571 reviews" sx={{ mb: 2 }} />

          <Divider sx={{ my: 1 }} />
          <List dense>
            <ListItemButton component={RouterLink} to="/" onClick={toggleMobileMenu} selected={isActive('/')}>
              <ListItemIcon><HomeIcon /></ListItemIcon>
              <ListItemText primary="Home" />
            </ListItemButton>
            <ListItemButton
              component={RouterLink}
              to="/payments"
              onClick={toggleMobileMenu}
              selected={isActive('/payments')}
            >
              <ListItemIcon><AccountBalanceIcon /></ListItemIcon>
              <ListItemText primary="Payments" />
            </ListItemButton>

            {/* Services group */}
            <ListItemButton onClick={() => setOpenServicesMobile((p) => !p)}>
              <ListItemIcon><PeopleAltOutlined /></ListItemIcon>
              <ListItemText primary="Services" />
              {openServicesMobile ? <ExpandLess /> : <ExpandMoreIcon />}
            </ListItemButton>
            <Collapse in={openServicesMobile} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {services.map((s) => (
                  <ListItemButton
                    key={s.href}
                    sx={{ pl: 6 }}
                    component={RouterLink}
                    to={s.href}
                    onClick={toggleMobileMenu}
                    selected={isActive(s.href)}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>{s.icon}</ListItemIcon>
                    <ListItemText primary={s.label} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>

            {/* Company group */}
            <ListItemButton onClick={() => setOpenCompanyMobile((p) => !p)}>
              <ListItemIcon><InfoOutlined /></ListItemIcon>
              <ListItemText primary="Company" />
              {openCompanyMobile ? <ExpandLess /> : <ExpandMoreIcon />}
            </ListItemButton>
            <Collapse in={openCompanyMobile} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {company.map((c) => (
                  <ListItemButton
                    key={c.href}
                    sx={{ pl: 6 }}
                    component={RouterLink}
                    to={c.href}
                    onClick={toggleMobileMenu}
                    selected={isActive(c.href)}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>{c.icon}</ListItemIcon>
                    <ListItemText primary={c.label} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </List>

          <Divider sx={{ my: 2 }} />
          <Stack spacing={1}>
            <Button
              component={RouterLink}
              to="/sellers/valuation"
              variant="contained"
              onClick={toggleMobileMenu}
            >
              Request Valuation
            </Button>
            <Button
              component={RouterLink}
              to="/repairs"
              startIcon={<BuildIcon />}
              onClick={toggleMobileMenu}
              color="inherit"
            >
              Report a Repair
            </Button>
            <Button
              component={RouterLink}
              to="/contact"
              startIcon={<CallIcon />}
              onClick={toggleMobileMenu}
              color="inherit"
            >
              Contact Us
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Registered office: 142 Victoria Road, Middlesbrough, TS1 3HU · Company type: Private limited · Status: Active
          </Typography>
        </Box>
      </Drawer>
    </>
  );
}
